import { z } from 'zod';

import { bubbleMutationOperationReceiptSchema } from '@/shared/api';

export const adminUsersSummarySchema = z.object({
  total: z.number(),
  admins: z.number(),
  developers: z.number(),
  systemClients: z.number(),
  calculatedAt: z.iso.datetime(),
  maxAgeSeconds: z.literal(30),
});

export const adminUsersItemSchema = z.object({
  bubbleUserId: z.string().nullable(),
  businessRoles: z.array(z.string()),
  businessRoleAssignments: z.array(
    z.object({
      assignmentId: z.string().nullable(),
      roleId: z.string(),
      roleKey: z.string(),
      displayName: z.string(),
      source: z.enum(['bubble', 'postgres']),
      lifecycleStatus: z.enum(['active', 'inactive', 'retired']),
      bubbleRoleId: z.string().nullable(),
      assignmentSource: z.enum(['bubble', 'postgres']),
      assignedAt: z.string().nullable(),
      isPrimary: z.boolean(),
    })
  ),
  id: z.string(),
  name: z.string().nullable(),
  email: z.string(),
  systemRole: z.enum(['SYSTEM_ADMIN', 'DEVELOPER', 'SYSTEM_CLIENT']),
  isOnline: z.boolean(),
  createdAt: z.string(),
});

export const adminUsersPaginationSchema = z.object({
  page: z.number(),
  pageSize: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
});

export const adminUsersDirectorySchema = z.object({
  items: z.array(adminUsersItemSchema),
  pagination: adminUsersPaginationSchema,
});

/** @deprecated The wire contract is now split into directory and summary resources. */
export const adminUsersDashboardSchema = adminUsersDirectorySchema.extend({
  summary: adminUsersSummarySchema,
});

export const adminUserRolePatchStatusSchema = z.object({
  pending: z.number(),
  processing: z.number(),
  retry: z.number(),
  succeeded: z.number(),
  dead: z.number(),
  latestStatus: z.string().nullable(),
  latestError: z.string().nullable(),
});

export const adminUserRoleCatalogItemSchema = z.object({
  id: z.string(),
  roleKey: z.string(),
  displayName: z.string(),
  source: z.enum(['bubble', 'postgres']),
  lifecycleStatus: z.enum(['active', 'inactive', 'retired']),
  bubbleRoleId: z.string().nullable(),
  canAssign: z.boolean(),
  isHighRisk: z.boolean(),
  accessOptions: z
    .array(
      z.object({
        id: z.string(),
        displayName: z.string(),
        domain: z.string().nullable(),
        purpose: z.string().nullable(),
      })
    )
    .optional(),
  accessLabels: z.array(z.string()),
  accessCount: z.number(),
});

export const adminUserRoleAssignmentSchema = z.object({
  assignmentId: z.string().nullable(),
  roleId: z.string(),
  roleKey: z.string(),
  displayName: z.string(),
  source: z.enum(['bubble', 'postgres']),
  lifecycleStatus: z.enum(['active', 'inactive', 'retired']),
  bubbleRoleId: z.string().nullable(),
  assignmentSource: z.enum(['bubble', 'postgres']),
  assignedAt: z.string().nullable(),
  isPrimary: z.boolean(),
});

export const adminUserRoleAssignmentPreviewSchema = z.object({
  added: z.array(adminUserRoleCatalogItemSchema),
  removed: z.array(adminUserRoleCatalogItemSchema),
  unchanged: z.array(adminUserRoleCatalogItemSchema),
  highRiskChanges: z.array(adminUserRoleCatalogItemSchema),
  requiresConfirmation: z.boolean(),
});

export const adminUserRoleAssignmentsSchema = z.object({
  user: z.object({
    bubbleUserId: z.string().nullable(),
    email: z.string(),
    userId: z.string(),
  }),
  systemRole: z.enum(['SYSTEM_ADMIN', 'DEVELOPER', 'SYSTEM_CLIENT']),
  catalog: z.array(adminUserRoleCatalogItemSchema),
  assignedRoleIds: z.array(z.string()),
  primaryRoleId: z.string().nullable(),
  assignments: z.array(adminUserRoleAssignmentSchema),
  preview: adminUserRoleAssignmentPreviewSchema,
  bubblePatchStatus: adminUserRolePatchStatusSchema.nullable(),
});

export const updateAdminUserRoleAssignmentsResponseSchema = adminUserRoleAssignmentsSchema.extend({
  success: z.boolean(),
  operation: bubbleMutationOperationReceiptSchema.nullable(),
});

export type AdminUserRoleAssignmentsResponse = z.infer<typeof adminUserRoleAssignmentsSchema>;
export type UpdateAdminUserRoleAssignmentsResponse = z.infer<
  typeof updateAdminUserRoleAssignmentsResponseSchema
>;
export type AdminUserRoleCatalogItem = z.infer<typeof adminUserRoleCatalogItemSchema>;
export type AdminUserRoleAssignment = z.infer<typeof adminUserRoleAssignmentSchema>;
export type UpdateAdminUserRoleAssignmentsInput = {
  allowSelfRoleChange?: boolean;
  confirmHighRisk?: boolean;
  confirmProduction?: boolean;
  reason?: string | null;
  requestId: string;
  roleIds: string[];
  primaryRoleId?: string | null;
};

export type AdminUsersDirectoryResponse = z.infer<typeof adminUsersDirectorySchema>;
export type AdminUsersSummaryResponse = z.infer<typeof adminUsersSummarySchema>;
/** @deprecated Use AdminUsersDirectoryResponse and AdminUsersSummaryResponse. */
export type AdminUsersDashboardResponse = {
  summary: AdminUsersSummaryResponse;
  items: AdminUsersDirectoryResponse['items'];
  pagination: AdminUsersDirectoryResponse['pagination'];
};
export type AdminUsersItem = z.infer<typeof adminUsersItemSchema>;

export const adminUserDeletionReceiptSchema = z.object({
  operationId: z.uuid(),
  status: z.literal('queued'),
});
export const adminUserDeletionStatusSchema = z.object({
  operationId: z.uuid(),
  status: z.enum(['queued', 'deleting', 'cleaning', 'completed', 'blocked', 'failed']),
  message: z.string().nullable().optional(),
});
export type AdminUserDeletionReceipt = z.infer<typeof adminUserDeletionReceiptSchema>;
